'use client';

import { type JSX } from 'react';

import { useKeyPressEvent } from 'react-use';

import Button from '@/components/ui/Button';
import HStack from '@/components/ui/HStack';
import Link from '@/components/ui/Link';
import NewTextInput from '@/components/ui/NewTextInput';
import Text from '@/components/ui/Text';
import appIcons from '@/constants/appIcons';
import useI18n from '@/hooks/useI18n';

import useForgotPassword from '../hooks/useForgotPassword';
import { authRoutes } from '../routes/authRoutes';

export default function ForgotPasswordForm(): JSX.Element {
    // ----------------------------------------------------------------------------------------------------
    // MARK: States & Constants
    // ----------------------------------------------------------------------------------------------------
    const { t } = useI18n();

    const { isValid, isLoading, error, validationErrors, submit, register } = useForgotPassword();

    // ----------------------------------------------------------------------------------------------------
    // MARK: Side Effects
    // ----------------------------------------------------------------------------------------------------
    // Press on enter to submit
    useKeyPressEvent('Enter', () => {
        if (isValid && !isLoading) {
            submit();
        }
    });

    // ----------------------------------------------------------------------------------------------------
    // MARK: Main Component UI
    // ----------------------------------------------------------------------------------------------------
    return (
        <div className="space-y-10">
            <div className="space-y-6">
                <NewTextInput
                    register={register('identifier')}
                    type="email"
                    label={t('Auth.Email_Label')}
                    placeholder={t('Auth.Email_Placeholder')}
                    prefixIcon={appIcons.outline.user}
                    errorMessage={validationErrors.identifier?.message}
                    isRequired
                />

                <Button verticalPadding="lg" width="full" isLoading={isLoading} isDisabled={!isValid} onClick={submit}>
                    {t('Auth.Forgot_Password_Button_Title')}
                </Button>

                {/* Error Message */}
                {error && (
                    <Text size="2xs" color="danger">
                        {error.message}
                    </Text>
                )}
            </div>

            <HStack justify="center" className="w-full space-x-1">
                <Text size="sm">{t('Auth.Remember_Your_Password')}</Text>
                <Link href={authRoutes.app.login()} color="accent">
                    {t('Auth.Login')}
                </Link>
            </HStack>
        </div>
    );
}
